import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Box,
  Divider,
  alpha,
  Typography,
  Chip
} from '@mui/material';
import { 
  Person as PersonIcon, 
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  VerifiedUser as VerifiedUserIcon,
  Assignment as AssignmentIcon,
  FileCopy as FileCopyIcon,
  HomeWork as HomeWorkIcon,
  ReportProblem as ReportProblemIcon,
  NoteAlt as NoteAltIcon,
  Scanner as ScannerIcon,
  FolderOpen as FolderOpenIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import proxyClient, { getAccessToken } from '../utils/proxyClient';

// Soft light theme colors
const ACCENT_COLOR = '#3f87f5'; // Updated blue to match sidebar
const TEXT_COLOR = '#000000'; // Black text color
const ICON_ACTIVE_COLOR = '#3f87f5'; // Updated to match sidebar blue
const ICON_INACTIVE_COLOR = '#424242'; // Dark gray for inactive icons

type Tab = 'dashboard' | 'borrowerForm' | 'fileUnderwriting' | 'uwNotes' | 'appraisalCR' | 'exceptionWriting' | 'businessNotes' | 'ocrReader' | 'easyAccess' | 'downloads' | 'admin';

interface SidebarProps {
  onTabChange: (tab: Tab) => void;
  onLogout: () => void;
  isAdmin?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ onTabChange, onLogout, isAdmin }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const getUserEmail = async () => {
      try {
        console.log('Sidebar: Fetching user email');
        const { data } = await proxyClient.auth.getUser();
        const user = data?.user;
        setUserEmail(user?.email || null);
        console.log('Sidebar: User email set to', user?.email || 'null');
      } catch (error) {
        console.error('Error getting user email:', error);
      }
    };
    getUserEmail();
  }, []);

  // Update the active tab based on the current URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('dashboard')) setActiveTab('dashboard');
    else if (path.includes('borrower-form')) setActiveTab('borrowerForm');
    else if (path.includes('file-underwriting')) setActiveTab('fileUnderwriting');
    else if (path.includes('uw-notes')) setActiveTab('uwNotes');
    else if (path.includes('appraisal-cr')) setActiveTab('appraisalCR');
    else if (path.includes('exception-writing')) setActiveTab('exceptionWriting');
    else if (path.includes('business-notes')) setActiveTab('businessNotes');
    else if (path.includes('ocr-reader')) setActiveTab('ocrReader');
    else if (path.includes('easy-access')) setActiveTab('easyAccess');
    else if (path.includes('downloads')) setActiveTab('downloads');
    else if (path.includes('admin')) setActiveTab('admin');
  }, [location.pathname]);
  
  const handleMouseEnter = () => {
    setExpanded(true);
  };
  
  const handleMouseLeave = () => {
    setExpanded(false);
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    onTabChange(tab);
  };

  const handleLogout = async () => {
    try {
      console.log('Sidebar: Logging out...');
      await proxyClient.auth.signOut();
      console.log('Sidebar: Logged out successfully, redirecting to login page');
      navigate('/login');
      onLogout();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const drawerWidth = expanded ? 240 : 60;
  
  const getListItemStyle = (tab: Tab) => {
    const isActive = activeTab === tab;
    return {
      m: 1,
      borderRadius: 0,
      color: isActive ? ACCENT_COLOR : alpha(TEXT_COLOR, 0.9),
      background: isActive ? '#F0F7FF' : 'transparent',
      borderLeft: isActive ? `4px solid ${ACCENT_COLOR}` : '4px solid transparent',
      transition: 'all 0.25s ease',
      backdropFilter: 'none',
      boxShadow: 'none',
      '&:hover': {
        background: isActive ? '#F0F7FF' : '#F5F9FF',
        boxShadow: 'none',
        transform: 'none',
        borderLeft: `4px solid ${ACCENT_COLOR}`,
        color: ACCENT_COLOR
      }
    };
  };
  
  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      action: 'dashboard'
    },
    {
      text: 'New Deal',
      icon: <AssignmentIcon />,
      action: 'borrowerForm'
    },
    {
      text: 'UW Notes',
      icon: <FileCopyIcon />,
      action: 'uwNotes'
    },
    {
      text: 'Appraisal CR',
      icon: <HomeWorkIcon />,
      action: 'appraisalCR'
    },
    {
      text: 'Exceptions',
      icon: <ReportProblemIcon />,
      action: 'exceptionWriting'
    },
    {
      text: 'Business Notes',
      icon: <NoteAltIcon />,
      action: 'businessNotes'
    },
    {
      text: 'OCR Reader',
      icon: <ScannerIcon />,
      action: 'ocrReader'
    },
    {
      text: 'Templates',
      icon: <FolderOpenIcon />,
      action: 'easyAccess'
    },
    {
      text: 'Downloads',
      icon: <DownloadIcon />,
      action: 'downloads'
    }
  ];
  
  return (
    <Drawer
      variant="permanent"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          background: 'linear-gradient(135deg, rgba(240, 240, 240, 0.99) 0%, rgba(220, 220, 220, 0.98) 100%)',
          backgroundImage: `
            radial-gradient(at 20% 30%, rgba(192, 192, 192, 0.94) 0px, transparent 50%),
            radial-gradient(at 80% 70%, rgba(255, 255, 255, 0.96) 0px, transparent 50%)
          `,
          boxShadow: 'inset -2px 0 5px rgba(0, 0, 0, 0.08)',
          borderRight: '1px solid rgba(192, 192, 192, 0.8)',
          width: drawerWidth,
          overflowX: 'hidden',
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginTop: '100px',
          '&::before': {
            display: 'none'
          },
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100% - 100px)'
        },
      }}
    >
      <Box sx={{ 
        overflow: 'auto', 
        position: 'relative', 
        zIndex: 1,
        scrollbarWidth: 'none',  /* Firefox */
        '&::-webkit-scrollbar': {
          display: 'none'  /* Chrome, Safari, Edge */
        },
        msOverflowStyle: 'none'  /* IE and Edge */
      }}>
        <List>
          {menuItems.map((item, index) => (
            <ListItem 
              key={index}
              button 
              onClick={() => handleTabChange(item.action as Tab)}
              sx={getListItemStyle(item.action as Tab)}
            >
              <ListItemIcon sx={{ 
                color: activeTab === item.action ? ICON_ACTIVE_COLOR : ICON_INACTIVE_COLOR,
                minWidth: expanded ? 36 : 'auto',
                ml: expanded ? 0 : 0.5,
                fontSize: '1.5rem',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.5rem'
                }
              }}>
                {item.icon}
              </ListItemIcon>
              {expanded && <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontSize: '1rem', 
                  fontFamily: "'Poppins', 'Roboto', sans-serif",
                  fontWeight: activeTab === item.action ? 700 : 500,
                  letterSpacing: 0.5
                }} 
              />}
            </ListItem>
          ))}
        </List>
        <Divider sx={{ 
          my: 2.5, 
          backgroundColor: 'rgba(0, 0, 0, 0.12)',
          opacity: 0.8,
          height: '2px',
          width: '80%',
          mx: 'auto',
          borderRadius: '2px'
        }} />
        <List>
          {isAdmin && (
            <ListItem 
              button 
              onClick={() => handleTabChange('admin')}
              sx={getListItemStyle('admin')}
            >
              <ListItemIcon sx={{ 
                color: activeTab === 'admin' ? ICON_ACTIVE_COLOR : ICON_INACTIVE_COLOR,
                minWidth: expanded ? 36 : 'auto',
                ml: expanded ? 0 : 0.5,
                fontSize: '1.5rem',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.5rem'
                }
              }}>
                <AdminIcon />
              </ListItemIcon>
              {expanded && <ListItemText 
                primary="Admin Panel" 
                primaryTypographyProps={{ 
                  fontSize: '1rem', 
                  fontFamily: "'Poppins', 'Roboto', sans-serif",
                  fontWeight: activeTab === 'admin' ? 700 : 500,
                  letterSpacing: 0.5
                }} 
              />}
            </ListItem>
          )}
          <ListItem 
            button 
            onClick={handleLogout}
            sx={{
              m: 1,
              borderRadius: 0,
              color: alpha('#000000', 0.9),
              background: 'transparent',
              backdropFilter: 'none',
              transition: 'all 0.25s ease',
              '&:hover': {
                background: '#F5F9FF',
                boxShadow: 'none',
                transform: 'none',
                color: ACCENT_COLOR,
                borderLeft: `4px solid ${ACCENT_COLOR}`
              }
            }}
          >
            <ListItemIcon sx={{ 
              color: '#000000',
              minWidth: expanded ? 36 : 'auto',
              ml: expanded ? 0 : 0.5,
              fontSize: '1.5rem',
              '& .MuiSvgIcon-root': {
                fontSize: '1.5rem'
              },
              '&:hover': {
                color: ACCENT_COLOR
              }
            }}>
              <LogoutIcon />
            </ListItemIcon>
            {expanded && <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{ 
                fontSize: '1rem',
                fontFamily: "'Poppins', 'Roboto', sans-serif",
                fontWeight: 700,
                color: '#000000',
                letterSpacing: 0.5
              }} 
            />}
          </ListItem>
        </List>
      </Box>
      
      {/* Authentication Status Panel */}
      {expanded && (
        <Box sx={{
          p: 2,
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ fontSize: '1rem', color: ICON_INACTIVE_COLOR }} />
            <Typography
              variant="caption"
              sx={{
                fontFamily: "'Roboto Condensed', sans-serif",
                color: ICON_INACTIVE_COLOR,
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {userEmail}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAdmin ? (
              <>
                <AdminIcon sx={{ fontSize: '1rem', color: '#2E7D32' }} />
                <Chip
                  size="small"
                  label="Admin"
                  color="success"
                  icon={<VerifiedUserIcon sx={{ fontSize: '0.875rem !important' }} />}
                  sx={{ 
                    height: 24,
                    '& .MuiChip-label': { 
                      px: 1,
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }
                  }}
                />
              </>
            ) : (
              <>
                <PersonIcon sx={{ fontSize: '1rem', color: ACCENT_COLOR }} />
                <Chip
                  size="small"
                  label="User"
                  color="primary"
                  sx={{ 
                    height: 24,
                    '& .MuiChip-label': { 
                      px: 1,
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }
                  }}
                />
              </>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar; 